import React, { FC, useEffect, useState, useCallback, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Text } from '@amzn/storm-ui';
import { useBundle } from '@amzn/react-arb-tools';
import { getActionEventTarget } from '@amzn/aac-chrome-api';
import RegistrationTitle from '@features/account-registration/components/RegistrationTitle';
import useChrome from '../../shared/chrome/hooks/useChrome';
import { ChromeKeys } from '../../shared/chrome/enums';
import { useCreateManagerAccountMutation } from '@services/account/account';
import {
  ENTITY_NAME_MAX_LENGTH,
  NO_BLANK_SPACE_REGEX,
} from '../../shared/constants';
import InputController from '../../shared/components/form/InputController';
import DropdownController from '../../shared/components/form/DropdownController';
import {
  MANAGER_ACCOUNT_REGISTRATION_PAGE_ID,
  MANAGER_ACCOUNT_REGISTRATION_TITLE_TRANSLATION_KEY,
  MANAGER_ACCOUNT_NAME_LABEL_TRANSLATION_KEY,
  MANAGER_ACCOUNT_NAME_REQUIRED_TRANSLATION_KEY,
  MANAGER_ACCOUNT_NAME_MAX_LENGTH_TRANSLATION_KEY,
  MANAGER_ACCOUNT_NAME_BLANK_TRANSLATION_KEY,
  MANAGER_ACCOUNT_TYPE_LABEL_TRANSLATION_KEY,
  MANAGER_ACCOUNT_TYPE_PLACEHOLDER_TRANSLATION_KEY,
  MANAGER_ACCOUNT_TYPE_OWNER_TRANSLATION_KEY,
  MANAGER_ACCOUNT_TYPE_AGENCY_TRANSLATION_KEY,
  MANAGER_ACCOUNT_CREATION_ERROR_TRANSLATION_KEY,
} from '../managerAccountRegistration/constants';
import {
  ManagerRegistrationPageWrapper,
  ManagerRegistrationPageInnerWrapper,
} from '../managerAccountRegistration/style';

const SUBMIT_ACTION_ID = 'adspSubmitCreateManagerAccount';
const CANCEL_ACTION_ID = 'adspCancelCreateManagerAccount';

interface ManagerAccountFormValues {
  managerAccountName: string;
  managerAccountType: 'Owner' | 'Agency';
}

const DspCreateManagerAccountPage: FC = () => {
  const [intl, isBundleLoading] = useBundle('pages.managerAccountRegistration');
  const [createManagerAccount] = useCreateManagerAccountMutation();
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useChrome(ChromeKeys.DspCreateManagerAccount);

  const methods = useForm<ManagerAccountFormValues>({
    defaultValues: {
      managerAccountName: '',
      managerAccountType: 'Owner',
    },
  });

  const onSubmit = useCallback(
    async (formData: ManagerAccountFormValues) => {
      setError(null);
      try {
        const result = await createManagerAccount(formData).unwrap();
        window.location.href = result.data.successUrl;
      } catch {
        setError(
          intl.getMessage(MANAGER_ACCOUNT_CREATION_ERROR_TRANSLATION_KEY),
        );
      }
    },
    [createManagerAccount, intl],
  );

  useEffect(() => {
    const actionEventTarget = getActionEventTarget();
    const handleSubmit = () => formRef.current?.requestSubmit();
    const handleCancel = () => {
      window.location.href = '/';
    };

    actionEventTarget.addEventListener(SUBMIT_ACTION_ID, handleSubmit);
    actionEventTarget.addEventListener(CANCEL_ACTION_ID, handleCancel);

    return () => {
      actionEventTarget.removeEventListener(SUBMIT_ACTION_ID, handleSubmit);
      actionEventTarget.removeEventListener(CANCEL_ACTION_ID, handleCancel);
    };
  }, []);

  if (isBundleLoading) {
    return null;
  }

  const accountTypeOptions = [
    {
      value: 'Owner',
      label: intl.getMessage(MANAGER_ACCOUNT_TYPE_OWNER_TRANSLATION_KEY),
    },
    {
      value: 'Agency',
      label: intl.getMessage(MANAGER_ACCOUNT_TYPE_AGENCY_TRANSLATION_KEY),
    },
  ];

  return (
    <ManagerRegistrationPageWrapper
      id={MANAGER_ACCOUNT_REGISTRATION_PAGE_ID}
      data-testid={MANAGER_ACCOUNT_REGISTRATION_PAGE_ID}
    >
      <ManagerRegistrationPageInnerWrapper>
        <FormProvider {...methods}>
          <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit)}>
            <RegistrationTitle
              title={intl.getMessage(
                MANAGER_ACCOUNT_REGISTRATION_TITLE_TRANSLATION_KEY,
              )}
            />
            <InputController
              label={intl.getMessage(
                MANAGER_ACCOUNT_NAME_LABEL_TRANSLATION_KEY,
              )}
              id="manager-account-name"
              controllerName="managerAccountName"
              registerOptions={{
                required: intl.getMessage(
                  MANAGER_ACCOUNT_NAME_REQUIRED_TRANSLATION_KEY,
                ),
                maxLength: {
                  value: ENTITY_NAME_MAX_LENGTH,
                  message: intl.getMessage(
                    MANAGER_ACCOUNT_NAME_MAX_LENGTH_TRANSLATION_KEY,
                  ),
                },
                pattern: {
                  value: NO_BLANK_SPACE_REGEX,
                  message: intl.getMessage(
                    MANAGER_ACCOUNT_NAME_BLANK_TRANSLATION_KEY,
                  ),
                },
              }}
            />
            <DropdownController
              label={intl.getMessage(
                MANAGER_ACCOUNT_TYPE_LABEL_TRANSLATION_KEY,
              )}
              overrideDropdownLabel={intl.getMessage(
                MANAGER_ACCOUNT_TYPE_PLACEHOLDER_TRANSLATION_KEY,
              )}
              id="manager-account-type"
              controllerName="managerAccountType"
              items={accountTypeOptions}
            />
            {error && <Text>{error}</Text>}
          </form>
        </FormProvider>
      </ManagerRegistrationPageInnerWrapper>
    </ManagerRegistrationPageWrapper>
  );
};

export default DspCreateManagerAccountPage;
